import Mark from 'mark.js';
import Cookies from 'universal-cookie';
import DOMPurify from 'dompurify';
import { isJson } from './JsonUtils';

declare global {
  interface Window {
    constants: unknown;
  }
}

export const PRODUCT_URL = 'https://www.responsive.io/product/profile-center/?utm_source=trust&utm_medium=footer&utm_campaign=branding';

export const getGuestUserData = () => {
  const profileId = sessionStorage.getItem('profileId');
  const key = profileId ?? 'guestUserData';
  let data = localStorage.getItem(key);
  if (!data && key !== 'guestUserData') {
    data = localStorage.getItem('guestUserData');
  }
  if (data && isJson(data)) {
    return JSON.parse(data);
  }
  return {};
};

export const getUserName = () => {
  const guestUserData = getGuestUserData();
  const userName = guestUserData && guestUserData?.companyGuestVO?.guest;
  return userName || '';
};

export const kFormatter = function kFormatter(num: number) {
  return num > 999 ? `${(num / 1000).toFixed(1)}k` : num;
};

const isInViewport = (element: Element | null) => {
  if (element) {
    const rect = element.getBoundingClientRect();
    return rect.top >= 0 && rect.left >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && rect.right <= (window.innerWidth || document.documentElement.clientWidth);
  }
  return false;
};

export const scrollIntoViewIfNeeded = (element: Element | null, block?: ScrollLogicalPosition, behavior?: ScrollBehavior) => {
  if (!element) {
    return;
  }
  if (isInViewport(element)) {
    element.scrollIntoView({ block: block || 'nearest', behavior: behavior || 'smooth' });
  } else {
    element.scrollIntoView({ block: block || 'center', behavior: behavior || 'smooth', inline: 'center' });
  }
};

const HIGHLIGHT_DURATION_MS = 3000;
const HIGHLIGHT_CLASSES = ['highlight-elem', 'border-grey'] as const;

const HIGHLIGHT_TARGET_SELECTOR = '[data-qa="COMPLIANCE"], [data-qa="CONTROLS"], .MuiCard-root, .MuiAccordion-root';

const getHighlightTarget = (element: Element): HTMLElement => {
  const el = element as HTMLElement;
  if (el.matches?.(HIGHLIGHT_TARGET_SELECTOR)) {
    return el;
  }
  const nestedHighlightTarget = element.querySelector(HIGHLIGHT_TARGET_SELECTOR);
  return (nestedHighlightTarget as HTMLElement) || el;
};

const applyHighlightClasses = (element: HTMLElement) => {
  HIGHLIGHT_CLASSES.forEach(className => element.classList.add(className));
};

const removeHighlightClasses = (element: HTMLElement) => {
  HIGHLIGHT_CLASSES.forEach(className => element.classList.remove(className));
};

export const scrollAndHighlightElement = (element: Element | null, options?: { block?: ScrollLogicalPosition; onComplete?: () => void; expandContentAccordion?: boolean }) => {
  if (!element) {
    return;
  }

  if (options?.expandContentAccordion) {
    const accordion = element.querySelector('.MuiAccordionSummary-root');
    (accordion as HTMLElement)?.click();
  }

  const highlightTarget = getHighlightTarget(element);
  applyHighlightClasses(highlightTarget);
  scrollIntoViewIfNeeded(element, options?.block || 'nearest', 'smooth');

  setTimeout(() => {
    removeHighlightClasses(highlightTarget);
    options?.onComplete?.();
  }, HIGHLIGHT_DURATION_MS);
};

export const scrollAndHighlightBySelector = (selector: string, options?: { block?: ScrollLogicalPosition; onComplete?: () => void }) => {
  scrollAndHighlightElement(document.querySelector(selector), options);
};

export const scrollAndHighlightByAttribute = (attrType: string, attrValue: string, options?: { block?: ScrollLogicalPosition; onComplete?: () => void }) => {
  scrollAndHighlightElement(document.querySelector(`[${attrType}="${attrValue}"]`), {
    ...options,
    expandContentAccordion: attrType === 'content-id'
  });
};

export const scrollToElem = (id: string, callback: () => void, block?: ScrollLogicalPosition) => {
  const element = document.querySelector(id);
  scrollAndHighlightElement(element, { block: block || 'center', onComplete: callback });
};

export const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const validateEmail = (email: string) => {
  return email.match(EMAIL_REGEX);
};

const cleanMergeTagSpan = function cleanMergeTagSpan(elemContent: string) {
  const tempElem = document.createElement('p');
  tempElem.innerHTML = elemContent;

  tempElem.querySelectorAll('span[merge-tag], div[merge-tag]').forEach(node => {
    const catalogId = node.querySelector('[catalog-id]')?.getAttribute('catalog-id') || node.getAttribute('catalog-id') || node.parentElement?.getAttribute('catalog-id');

    if (!catalogId) {
      const mergeTagName = node.getAttribute('merge-tag') || '';
      if (node.classList.contains('rfpio-autocomplete')) {
        node.textContent = `[${mergeTagName}]`;
        node.removeAttribute('merge-tag');
      } else {
        node.replaceWith(`[${mergeTagName}]`);
      }
    }
  });

  return tempElem.innerHTML;
};

const tagPattern = /\[([^\]\s]+)\]/gm;

export const updateMergeTagValue = (elemContent: string, mergetagValueMap: { [key: string]: string }) => {
  const cleanedContent = cleanMergeTagSpan(elemContent);
  let processedElemContent = cleanedContent.replace(tagPattern, (spanTag: string) => {
    return spanTag.replace(/<[^>]*>/gm, '');
  });
  const parentEle = document.createElement('div');
  parentEle.innerHTML = processedElemContent;
  processedElemContent = parentEle.innerHTML;
  let count = 0;
  const finalElement = processedElemContent.replace(tagPattern, (mergeTagName: string) => {
    const processedMergeTagName: string = mergeTagName?.replace(/[[\]]/g, '') || '';
    const mergeTagValue = mergetagValueMap?.[processedMergeTagName] || '';
    const tagId = count;
    count += 1;
    return `<span class="display-merge-element" tag-id=${tagId} merge-tag=${processedMergeTagName}>${mergeTagValue || mergeTagName}</span>`;
  });
  return finalElement;
};

export const extractAllMergeTagNames = (elemContent: string, mergeTagKeyList: string[] = []): string[] => {
  if (!elemContent || mergeTagKeyList.length === 0) {
    return [];
  }
  const cleanedContent = cleanMergeTagSpan(elemContent);
  const matches = cleanedContent.match(tagPattern) || [];
  const tagNames = matches.map(tag => tag.slice(1, -1)).filter(tagName => mergeTagKeyList.includes(tagName));

  return tagNames;
};

/** Null/empty → ''; strips tags for invite-only Access Denied empty checks. */
const toInviteOnlyAccessDeniedPlainText = (message?: string | null): string => {
  if (!message) {
    return '';
  }
  return message
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/** True when a stored invite-only Access Denied message should be shown instead of i18n default. */
export const hasCustomInviteOnlyAccessDeniedMessage = (message?: string | null): boolean => toInviteOnlyAccessDeniedPlainText(message).length > 0;

/** Replace [companyName] spans / plain tags and {{companyName}} before visitor display. */
const escapeInviteOnlyAccessDeniedText = (value: string): string => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

export const replaceInviteOnlyAccessDeniedMergeTags = (html: string, companyName?: string | null): string => {
  const value = escapeInviteOnlyAccessDeniedText(companyName ?? '');
  return (html || '')
    .replace(/<span[^>]*\brfpio-autocomplete\b[^>]*>\s*\[companyName\]\s*<\/span>/gi, value)
    .replace(/\{\{\s*companyName\s*\}\}/g, value)
    .replace(/\[companyName\]/gi, value);
};

/**
 * Display allowlist for the invite-only Access Denied message.
 * The editor relies on TinyMCE xss_sanitization; this still runs because stored HTML can bypass that editor.
 */
const INVITE_ONLY_ACCESS_DENIED_PURIFY_CONFIG = {
  ALLOWED_TAGS: ['a', 'b', 'br', 'div', 'em', 'i', 'li', 'ol', 'p', 'span', 'strong', 'ul'],
  ALLOWED_ATTR: ['class', 'href', 'merge-tag', 'rel', 'style', 'target', 'title']
};

/** Drop scripts, event handlers, and tags the invite-only editor cannot produce. */
export const sanitizeInviteOnlyAccessDeniedHtml = (html?: string | null): string => {
  if (!html) {
    return '';
  }
  return DOMPurify.sanitize(html, INVITE_ONLY_ACCESS_DENIED_PURIFY_CONFIG);
};

export const validEmailIdsList = (emailIdsList: string[]) => {
  const list: string[] = [];
  emailIdsList.forEach((id: string) => {
    if (validateEmail(id)) {
      list.push(id.toLowerCase());
    }
  });
  return list;
};

let timer: ReturnType<typeof setTimeout>;
export const debounceFunc = (func: (...args: unknown[]) => void, delay: number) => {
  return function debouncedFunc(...args: unknown[]) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

export const getItemsPerPage = (context: string, defaultLimit?: number) => {
  const localData: Record<string, number> = JSON.parse(localStorage.getItem('itemsPerPage') || '{}') as Record<string, number>;
  if (localData && localData[context]) {
    return localData[context];
  }
  localData[context] = defaultLimit || 25;
  localStorage.setItem('itemsPerPage', JSON.stringify(localData));
  return defaultLimit || 25;
};

export const setItemsPerPage = (context: string, itemsPerPage: number) => {
  const localData: Record<string, number> = JSON.parse(localStorage.getItem('itemsPerPage') || '{}') as Record<string, number>;
  localData[context] = itemsPerPage;
  localStorage.setItem('itemsPerPage', JSON.stringify(localData));
};

export const sleepSometime = (ms: number) => {
  return new Promise<void>(res => {
    const timeout = setTimeout(() => {
      res();
      clearTimeout(timeout);
    }, ms);
  });
};

export const capitalizeFirstLetter = (value: string): string => {
  if (!value || value.trim().length === 0) return '';
  return value.trim().charAt(0).toUpperCase() + value.trim().slice(1);
};

export const markJSOptions: Mark.MarkOptions = {
  accuracy: {
    value: 'exactly',
    limiters: [',', '.', ';', '-', ':', '_', '/', '[', ']', '"', '~', '?', '<', '>', '$', "'", '\\', '(', ')', '*', '%', '`', '^', '&', '#', '{', '}', '|', '!', '@', '+', '=', '’', '„']
  },
  element: 'span',
  className: 'highlight',
  acrossElements: true,
  ignoreJoiners: true,
  separateWordSearch: false
};

export const wordToHighlight = function wordToHighlight(searchKeyword: string, highlightWords: string[]) {
  let keyWords: string[] = [];
  if (highlightWords && Array.isArray(highlightWords) && highlightWords.length > 0) {
    keyWords = highlightWords;
  }
  if (searchKeyword !== null) {
    const regex = /[\u3000-\u303F]|[\u3040-\u309F]|[\u30A0-\u30FF]|[\uFF00-\uFFEF]|[\u4E00-\u9FAF]|[\u2605-\u2606]|[\u2190-\u2195]|\u203B/g;
    const keyword = searchKeyword;
    const result = keyword.match(regex);
    const accuracyObj = markJSOptions.accuracy as Mark.MarkAccuracyObject;
    if (result && result.length > 0) {
      accuracyObj.value = 'partially';
    } else {
      accuracyObj.value = 'exactly';
    }
    const exp = /".*?"/gm;
    let word = '';
    const lowercaseKey = searchKeyword.toLowerCase();
    let match: RegExpExecArray | null = exp.exec(lowercaseKey);
    while (match !== null) {
      const phrase: string = match[0].substring(1, match[0].length - 1);
      const trimmedPhrase: string = phrase.trim();
      if (!keyWords.includes(trimmedPhrase)) {
        keyWords.push(trimmedPhrase);

        if (trimmedPhrase.includes("'")) {
          const replacedWord = trimmedPhrase.replaceAll("'", '’');
          if (!keyWords.includes(replacedWord)) {
            keyWords.push(replacedWord);
          }
        }
      }
      match = exp.exec(lowercaseKey);
    }
    word = word.replace(/".*?"/gm, ' ');
    let tempKeyword = word.split(/[\s,.;:_/[\]"~-]/g);
    if (highlightWords && Array.isArray(highlightWords)) {
      tempKeyword = tempKeyword.concat(highlightWords);
    }
    tempKeyword.forEach((key: string) => {
      if (key.length > 0 && keyWords.indexOf(key) < 0 && !keyWords.includes(key)) {
        keyWords.push(key);

        if (key.includes("'")) {
          const replacedWord: string = key.replaceAll("'", '’');
          if (!keyWords.includes(replacedWord)) {
            keyWords.push(replacedWord);
          }
        }
      }
    });
  }
  return keyWords;
};

export const highlightWordsForContext = (keywords: string[], context: string, highlightOptions: Mark.MarkOptions) => {
  const mInstance = new Mark(context);
  mInstance.unmark({
    done: () => {
      mInstance.mark(keywords, highlightOptions);
    }
  });
};

export const markUpContent = (searchTerm: string, highlightWords: string[]) => {
  const wordsToHighlight = wordToHighlight(searchTerm, highlightWords);
  const highlightOptions = { ...markJSOptions } as Mark.MarkOptions;
  (highlightOptions as Record<string, unknown>).accuracy = '';
  highlightWordsForContext(wordsToHighlight, '.mark-up', highlightOptions);
};

export const scrollToElementForCB = (sectionId?: string, paramType?: string) => {
  let sId = null;
  let searchParams;
  const type = paramType || 'section-id';

  if (sectionId) {
    sId = sectionId;
  } else {
    searchParams = new URLSearchParams(window.location.search);
    sId = searchParams.get('sId');
  }

  if (!sId) {
    return;
  }

  const section = document.querySelector(`[${type}="${sId}"]`);

  if (section) {
    section.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    section.classList.add('highlight-elem');
    if (type === 'content-id') {
      const accordion = section.querySelector('.MuiAccordionSummary-root');
      if (accordion) {
        (accordion as HTMLElement).click();
      }
    }
  }
  if (searchParams) {
    searchParams.delete('sId');
  }
  const newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${searchParams ? searchParams.toString() : ''}`;
  window.history.replaceState({ path: newurl }, '', newurl);
};

const STYLE_TAG_ID = 'codex-hidden-sections-style';

const escapeAttrValue = (value: string) => value.replace(/[\\"]/g, '\\$&');

const syncStyle = (doc: Document, cssText: string): void => {
  const existing = doc.getElementById(STYLE_TAG_ID) as HTMLStyleElement | null;
  if (!cssText) {
    existing?.remove();
    return;
  }

  const style = existing ?? doc.createElement('style');
  style.id = STYLE_TAG_ID;
  style.textContent = cssText;
  if (!style.isConnected) {
    (doc.head || doc.documentElement).appendChild(style);
  }
};

export const hideEmptyDocumentSectionsBySectionId = (documentSectionIdsToHide: string[]) => {
  if (!documentSectionIdsToHide) {
    return;
  }

  const nextIds = documentSectionIdsToHide.map(id => String(id).trim()).filter(Boolean);
  const cssText = nextIds?.length ? `${nextIds.map(id => `[section-id="${escapeAttrValue(id)}"]`).join(',')} { display: none !important; }` : '';

  syncStyle(document, cssText);

  document.querySelectorAll('iframe').forEach((iframe: HTMLIFrameElement) => {
    try {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDoc) syncStyle(iframeDoc, cssText);
    } catch {
      // Cross-origin iframe, skip
    }

    if (iframe.dataset.codexHiddenSectionsHooked === '1') return;
    iframe.dataset.codexHiddenSectionsHooked = '1';

    iframe.addEventListener('load', () => {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) syncStyle(iframeDoc, (document.getElementById(STYLE_TAG_ID) as HTMLStyleElement | null)?.textContent || '');
      } catch {
        // Cross-origin iframe, skip
      }
    });
  });
};

export const pasteText = (event: React.ClipboardEvent<HTMLDivElement>) => {
  const clipboard = event.clipboardData;
  if (clipboard) {
    event.preventDefault();
    event.stopPropagation();
    let text = clipboard.getData('text/plain');
    text = text.replaceAll('\n', '<br/>');
    document.execCommand('insertHTML', false, text);
  }
};

export const getExtractedMsg = (text?: string) => {
  let extractedMessage: string = '';
  if (text) {
    const div = document.createElement('div');
    div.innerHTML = text || '';
    extractedMessage = (div.innerText || div.textContent) ?? '';
  }
  return extractedMessage.trim();
};

export const generateAndSetPreviewToken = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const token = Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
  const cookies = new Cookies();
  cookies.set('preview_token', token, {
    path: '/',
    maxAge: 24 * 60 * 60,
    sameSite: 'strict',
    secure: true
  });
  return token;
};

export enum AskActivities {
  RESPONSIVE_ASK = 'RESPONSIVE_ASK',
  CHILD_WINDOW_ASK_READY = 'CHILD_WINDOW_ASK_READY'
}
